/**
 * OFC Client Display Types
 *
 * Types for rendering OFC tables in client applications.
 * These extend the core engine types with UI-specific properties.
 */
import { Label, OFCVariant, RowPosition } from './enums';
import type { Board, Card } from './types';

// Re-export enums that are used in client types
export { RowPosition, OFCVariant, Label };

/**
 * OFC game phases for UI state management
 */
export enum OFCPhase {
  WAITING_FOR_PLAYERS = 'waiting_for_players',
  DEALING = 'dealing',
  PLACING = 'placing',
  ROUND_COMPLETE = 'round_complete',
  SHOWDOWN = 'showdown',
  HAND_COMPLETE = 'hand_complete',
}

/**
 * Player status for OFC
 */
export enum OFCPlayerStatus {
  WAITING = 'waiting',
  ACTING = 'acting',
  PLACED = 'placed',
  FOULED = 'fouled',
  IN_FANTASY = 'in_fantasy',
  SITTING_OUT = 'sitting_out',
}

/**
 * Card with selection state for drag/drop or click-to-place
 */
export interface OFCCardWithState extends Card {
  isSelected?: boolean;
  isPlaced?: boolean;
  slotTarget?: {
    row: RowPosition;
    slotIndex: number;
  };
}

/**
 * Row display data for UI
 */
export interface OFCRowDisplay {
  position: RowPosition;
  cards: (Card | null)[];
  maxCards: 3 | 5;
  handLabel?: Label;
  royaltyPoints?: number;
  isComplete?: boolean;
  /** Slot indices that were placed in the most recent round (for "Last Set" ribbon) */
  lastSetSlots?: number[];
}

/**
 * Complete board display data for UI
 */
export interface OFCBoardDisplay {
  playerIndex: number;
  front: OFCRowDisplay;
  middle: OFCRowDisplay;
  back: OFCRowDisplay;
  isFouled?: boolean;
  totalRoyalties?: number;
}

/**
 * Player data for OFC table display
 */
export interface OFCPlayerDisplay {
  seatIndex: number;
  playerIndex: number | null; // null for reserved/joining players not yet in a hand
  oddsUserId: string;
  alias: string;
  avatarUrl?: string;
  stack: number;
  status: OFCPlayerStatus;
  board: OFCBoardDisplay;
  holeCards: Card[];
  discardCount: number; // Number of cards discarded (for Pineapple)
  isDealer: boolean;
  isInFantasy: boolean;
  /** Consecutive fantasy hands count (1 = first fantasy, 2 = second in a row, etc.) */
  fantasyStreak?: number;
  fantasyCardCount?: number;
  score?: number;
  timeRemaining?: number;
  maxTime?: number;
  isDisconnected?: boolean;
  usingTimebank?: boolean;
  usingDisconnectionTimebank?: boolean;
  // Seat state flags for action labels
  reserved?: boolean;
  reservedUntil?: number;
  joining?: boolean;
  joiningUntil?: number;
  addingChips?: boolean;
}

/**
 * OFC available actions for a player
 */
export interface OFCAvailableActions {
  canPlace: boolean;
  requiredPlacements: number;
  requiredDiscards: number;
  availablePositions: RowPosition[];
  isFantasy: boolean;
  timeRemaining: number | null;
  usingTimebank: boolean;
  usingDisconnectionTimebank: boolean;
}

/**
 * Row result with hand label and royalty points
 */
export interface RowResult {
  label: string;
  royalty: number;
}

/**
 * Scoring result for a single player
 */
export interface PlayerScoringResult {
  playerIndex: number;
  score: number;
  fouled: boolean;
  rowResults: {
    front: RowResult;
    middle: RowResult;
    back: RowResult;
  };
  fantasy: {
    qualifiesForFantasy: boolean;
    continuesFantasy: boolean;
    nextHandCardCount: number;
  };
}

/**
 * Complete scoring results for all players
 */
export interface ScoringResults {
  players: PlayerScoringResult[];
  nextFantasyPlayers: number[];
}

/**
 * OFC Table state for client rendering
 */
export interface OFCTableState {
  tableId: string;
  tableName: string;
  variant: OFCVariant;
  maxPlayers: 2 | 3;
  phase: OFCPhase;
  roundIndex: number;
  players: OFCPlayerDisplay[];
  currentActorIndex: number | null;
  actingPlayerIndexes: number[];
  dealerSeatIndex: number;
  pointValue: number;
  isTournament: boolean;

  // Hero-specific state (only populated for the current player)
  heroAvailableActions?: OFCAvailableActions | null;
  heroCardIndexes?: number[]; // Card indexes for placement submission

  // Showdown state
  scoringResults?: ScoringResults | null;
}

/**
 * Placement action for a card
 */
export interface OFCPlacementAction {
  card: Card;
  row: RowPosition;
  slotIndex: number;
}

/**
 * Player's pending placements before confirmation
 */
export interface OFCPendingPlacements {
  placements: OFCPlacementAction[];
  discard?: Card;
}

/**
 * Score display data
 */
export interface OFCScoreDisplay {
  playerIndex: number;
  alias: string;
  rowScores: {
    front: number;
    middle: number;
    back: number;
  };
  royalties: number;
  scoopBonus: number;
  totalScore: number;
  netPoints: number;
}

/**
 * Fantasy qualification display
 */
export interface OFCFantasyDisplay {
  playerIndex: number;
  qualifies: boolean;
  triggerReason?: string;
  cardCount: number;
  continues?: boolean;
}

/**
 * CSS style properties for positioning (compatible with React.CSSProperties)
 */
export interface CSSPositionProperties {
  position?: 'absolute' | 'relative' | 'fixed' | 'static' | 'sticky';
  top?: string | number;
  right?: string | number;
  bottom?: string | number;
  left?: string | number;
  transform?: string;
  zIndex?: number;
}

/**
 * Seat position configuration for OFC tables
 * OFC needs different layout than standard poker due to the 3-row boards
 */
export interface OFCSeatPosition {
  seatIndex: number;
  /** Main player area (avatar + info panel) */
  playerAreaPosition: CSSPositionProperties;
  /** Board position relative to player area */
  boardPosition: CSSPositionProperties;
  /** Dealt cards position (cards in hand waiting to be placed) */
  dealtCardsPosition: CSSPositionProperties;
  /** Discard pile position */
  discardPosition: CSSPositionProperties;
  /** Dealer button position */
  dealerButtonPosition: CSSPositionProperties;
  /** Score display position */
  scorePosition: CSSPositionProperties;
}

/**
 * OFC card slot dimensions
 */
export interface OFCCardSlotDimensions {
  width: number;
  height: number;
  gap: number;
}

/**
 * OFC board dimensions based on card slot size
 */
export interface OFCBoardDimensions {
  cardSlot: OFCCardSlotDimensions;
  rowGap: number;
  totalWidth: number;
  totalHeight: number;
}

/**
 * Default card slot dimensions for desktop
 */
export const DEFAULT_CARD_SLOT_DESKTOP: OFCCardSlotDimensions = {
  width: 44,
  height: 60,
  gap: 4,
};

/**
 * Default card slot dimensions for mobile
 */
export const DEFAULT_CARD_SLOT_MOBILE: OFCCardSlotDimensions = {
  width: 36,
  height: 49,
  gap: 3,
};

/**
 * Calculate board dimensions based on card slot size
 */
export const calculateBoardDimensions = (cardSlot: OFCCardSlotDimensions): OFCBoardDimensions => {
  const rowGap = cardSlot.gap * 2;
  // Front row has 3 cards, middle/back have 5 cards
  const totalWidth = cardSlot.width * 5 + cardSlot.gap * 4;
  const totalHeight = cardSlot.height * 3 + rowGap * 2;

  return {
    cardSlot,
    rowGap,
    totalWidth,
    totalHeight,
  };
};

/**
 * Convert engine Board to OFCBoardDisplay for rendering
 */
export const boardToDisplay = (board: Board): OFCBoardDisplay => {
  const createRowDisplay = (position: RowPosition, cards: readonly Card[], maxCards: 3 | 5): OFCRowDisplay => ({
    position,
    cards: Array.from({ length: maxCards }, (_, i) => cards[i] ?? null),
    maxCards,
    isComplete: cards.length === maxCards,
  });

  return {
    playerIndex: board.playerIndex,
    front: createRowDisplay(RowPosition.FRONT, board.front.cards, 3),
    middle: createRowDisplay(RowPosition.MIDDLE, board.middle.cards, 5),
    back: createRowDisplay(RowPosition.BACK, board.back.cards, 5),
  };
};

/**
 * Create an empty board display for a player
 */
export const createEmptyBoardDisplay = (playerIndex: number): OFCBoardDisplay => ({
  playerIndex,
  front: {
    position: RowPosition.FRONT,
    cards: [null, null, null],
    maxCards: 3,
    isComplete: false,
  },
  middle: {
    position: RowPosition.MIDDLE,
    cards: [null, null, null, null, null],
    maxCards: 5,
    isComplete: false,
  },
  back: {
    position: RowPosition.BACK,
    cards: [null, null, null, null, null],
    maxCards: 5,
    isComplete: false,
  },
});
